import { JobLocation, StorageBoxLocation } from "../models/job";
import { ItemJob } from "../models/sales";
import { TransshipmentLocation } from "../models/transshipment";
import { StorageBoxLoadedTypeEnum } from "./enums/storageBox";

export type StorageBoxLocationFrom = {
  typeName: string;
  names: string[];
};

export const formatItemLocation = (item: ItemJob): string => {
  const hasStorageBoxes = !!item?.storageBoxes?.length;
  const hasMapCells = !!item?.mapCells?.length;

  if (!hasStorageBoxes && !hasMapCells) {
    const type = item?.storageBoxLoadedType?.trim();
    const value = item?.storageBoxLoadedValue?.trim();

    if (type?.toLowerCase() === StorageBoxLoadedTypeEnum.LIFTVAN.toLowerCase() && value) {
      return value;
    }

    return "";
  }

  if (!hasStorageBoxes && hasMapCells) {
    const mapName = item.mapCells?.[0]?.map?.name;
    const warehouseName = item.mapCells?.[0]?.map?.warehouse?.name;
    const sectionName = item.mapCells?.[0]?.map?.section?.name;
    const cell = item.mapCells?.[0]?.name;

    return `${warehouseName}. ${sectionName}. ${mapName}: ${cell}`;
  }

  const storageBoxNumber = item?.storageBoxes?.[0]?.number;
  const storageBoxType = item?.storageBoxes?.[0]?.storageBoxType?.name;

  return storageBoxType && storageBoxNumber
    ? `${storageBoxType.substring(0, 1).toUpperCase() + storageBoxType.substring(1)}${
        storageBoxNumber?.trim() ? ` - ${storageBoxNumber}` : ""
      }`
    : "";
};

export const formatJobLocation = (jobLocation: JobLocation): string[] => {
  const { mapCellsLocations, storageBoxLocations } = jobLocation;

  const names: string[] = [];

  mapCellsLocations.forEach((location) => {
    names.push(
      `${location.warehouseName}. ${location.sectionName}. ${
        location.mapName
      }: ${location?.names.join(", ")}`
    );
  });

  storageBoxLocations.forEach((location) => {
    names.push(
      `${location?.typeName}: ${location?.names
        ?.sort((nameA, nameB) => nameA.localeCompare(nameB))
        .join(", ")}`
    );
  });

  return names;
};

export const formatItemLocationFrom = (locations?: TransshipmentLocation[]): string[] => {
  if (!locations || !locations.length) return [""];

  const storageBoxLocationsFrom: StorageBoxLocation[] = [];

  locations?.forEach((location) => {
    const storageBoxLocation = storageBoxLocationsFrom?.find(
      (storageBoxLocation) =>
        storageBoxLocation?.typeId === location?.fromStorageBox?.storageBoxTypeId
    );

    if (!storageBoxLocation && location?.fromStorageBox?.storageBoxTypeId) {
      storageBoxLocationsFrom?.push({
        names: [location?.fromStorageBox?.number || ""],
        typeName: location?.fromStorageBox?.storageBoxType?.name || "",
        typeId: location?.fromStorageBox?.storageBoxTypeId,
      });
    } else if (!storageBoxLocation?.names?.includes(location?.fromStorageBox?.number || "")) {
      storageBoxLocation?.names.push(location?.fromStorageBox?.number || "");
    }

    if (
      location?.fromMapCell &&
      location?.fromMapCell?.name &&
      location?.fromMapCell?.map?.section?.name
    ) {
      const sectionLocation = storageBoxLocationsFrom?.find(
        (storageBoxLocation) =>
          storageBoxLocation?.typeName === location?.fromMapCell?.map?.section?.name
      );

      if (!sectionLocation) {
        storageBoxLocationsFrom.push({
          typeId: -1,
          typeName: location?.fromMapCell?.map?.section?.name,
          names: [location?.fromMapCell?.name ?? ""],
        });
      } else {
        sectionLocation.names.push(location?.fromMapCell?.name ?? "");
      }
    }
  });

  const namesFrom: string[] = [];

  storageBoxLocationsFrom.forEach((location) => {
    namesFrom.push(`${location?.typeName}: ${location?.names.join(", ")}`);
  });

  return namesFrom;
};

export const formatItemJobTo = (locations?: TransshipmentLocation[]) => {
  if (!locations || !locations.length) return [""];

  const storageBoxLocationsTo: StorageBoxLocation[] = [];

  locations?.forEach((location) => {
    const storageBoxLocation = storageBoxLocationsTo?.find(
      (storageBoxLocation) =>
        storageBoxLocation?.typeId === location?.toStorageBox?.storageBoxTypeId
    );

    if (!storageBoxLocation && location?.toStorageBox?.storageBoxTypeId) {
      storageBoxLocationsTo?.push({
        names: [location?.toStorageBox?.number || ""],
        typeName: location?.toStorageBox?.storageBoxType?.name || "",
        typeId: location?.toStorageBox?.storageBoxTypeId,
      });
    } else if (!storageBoxLocation?.names?.includes(location?.toStorageBox?.number || "")) {
      storageBoxLocation?.names.push(location?.toStorageBox?.number || "");
    }

    if (location?.toMapCell && location?.toMapCell?.name && location?.toMapCell?.map?.section) {
      const sectionLocation = storageBoxLocationsTo?.find(
        (storageBoxLocation) =>
          storageBoxLocation?.typeName === location?.toMapCell?.map?.section?.name
      );

      if (!sectionLocation) {
        storageBoxLocationsTo.push({
          typeId: -1,
          typeName: location?.toMapCell?.map?.section?.name,
          names: [location?.toMapCell?.name ?? ""],
        });
      } else {
        sectionLocation.names.push(location?.toMapCell?.name ?? "");
      }
    }
  });

  const namesTo: string[] = [];

  storageBoxLocationsTo.forEach((location) => {
    namesTo.push(`${location?.typeName}: ${location?.names.join(", ")}`);
  });

  return namesTo;
};
